import { FilterModel, GridApi } from 'ag-grid-community';
import { isEqual, isString } from 'lodash';

import { AgGridFilterOption } from 'core/enums';

interface IFilterCondition {
  filterType?: string;
  type?: string;
  filter?: unknown;
  filterTo?: unknown;
  conditions?: IFilterCondition[];
  filterModels?: (IFilterCondition | null)[];
  [key: string]: unknown;
}

const VALUELESS_TEXT_FILTER_OPTIONS: string[] = [
  AgGridFilterOption.BLANK,
  AgGridFilterOption.NOT_BLANK,
];

// Spaces inside the value stay as typed; a value of spaces only counts as no value.
export const trimFilterText = (value: string | undefined): string | undefined => value?.trim();

const trimTextCondition = (condition: IFilterCondition): IFilterCondition | null => {
  if (VALUELESS_TEXT_FILTER_OPTIONS.includes(condition.type)) {
    return condition;
  }

  const trimmedCondition = { ...condition };

  if (isString(condition.filter)) {
    trimmedCondition.filter = trimFilterText(condition.filter);
  }

  if (isString(condition.filterTo)) {
    trimmedCondition.filterTo = trimFilterText(condition.filterTo);
  }

  return trimmedCondition.filter === '' ? null : trimmedCondition;
};

const trimTextModel = (model: IFilterCondition): IFilterCondition | null => {
  if (!model?.conditions) {
    return trimTextCondition(model);
  }

  const conditions = model.conditions.map(trimTextCondition).filter(Boolean);

  if (conditions.length > 1) {
    return { ...model, conditions };
  }

  return conditions[0] ?? null;
};

const trimModel = (model: IFilterCondition): IFilterCondition | null => {
  switch (model?.filterType) {
    case 'text':
      return trimTextModel(model);
    case 'multi': {
      const filterModels = model.filterModels?.map((subModel) =>
        subModel ? trimModel(subModel) : subModel
      );

      return filterModels?.some(Boolean) ? { ...model, filterModels } : null;
    }
    default:
      return model;
  }
};

// Trims the text values of an AG Grid filter model and drops the ones left empty. Set, number,
// date and custom filter models pass through untouched: their values are exact options.
export const trimTextFilterModel = (
  filterModel: FilterModel | undefined
): FilterModel | undefined => {
  if (!filterModel) {
    return filterModel;
  }

  return Object.entries(filterModel).reduce<FilterModel>((trimmedFilterModel, [colId, model]) => {
    const trimmedModel = trimModel(model);

    if (trimmedModel) {
      trimmedFilterModel[colId] = trimmedModel;
    }

    return trimmedFilterModel;
  }, {});
};

// AG Grid's own trimInput keeps a value of spaces only as typed, and a model set through the API
// (a saved filter, a restored session) is never trimmed, so the grid is put back on the trimmed
// model. Returns true when it changed the model: the grid then raises a new filterChanged event.
export const applyTrimmedTextFilterModel = (gridApi: GridApi): boolean => {
  const filterModel = gridApi.getFilterModel() ?? {};
  const trimmedFilterModel = trimTextFilterModel(filterModel);

  if (isEqual(filterModel, trimmedFilterModel)) {
    return false;
  }

  gridApi.setFilterModel(trimmedFilterModel);

  return true;
};
